import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import Box from "@mui/material/Box";
import PostageDeliveryOption from "./PostageDeliveryOption";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import SchoolDeliveryOptions from "./SchoolDeliveryOptions";
import {
  CheckoutCard,
  CheckoutCardList,
  CheckoutCardTitle,
} from "./CheckoutCard";
import ListItemRadioButton from "../../../components/Common/ListItemRadioButton";
import { formatCurrency } from "../../../utils";
import { useCheckout } from "../../providers/CheckoutProvider";
import PickupDeliveryOptions from "./PickupDeliveryOptions";
import DigitalDeliveryOption from "./DigitalDeliveryOption";
import { Skeleton } from "@mui/material";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import { useTheme } from "@mui/material/styles";
import Address from "../Address";

const useStyles = makeStyles((theme) => ({
  deliveryMethodHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    "& > svg": {
      marginRight: theme.spacing(2),
    },
  },
  selected: {
    backgroundColor: "red",
    "&.MuiListItem-root, &.MuiListItem-root:active, &.MuiListItem-root:hover":
      {
        backgroundColor: theme.palette.secondary.light,
      },
  },
  deliveryMethodName: {
    flexGrow: 1,
  },
  deliveryPrice: {
    whiteSpace: "nowrap",
    fontWeight: 700,
    minWidth: "80px",
    textAlign: "right",
  },
  secondaryInfo: {
    fontSize: "0.8rem",
    color: "#777",
  },
  deliveryAddress: {
    fontStyle: "normal",
    "& .address--address": {
      fontSize: "0.8rem",
      color: "#777",
    },
  },
  selectedDeliveryOption: {
    display: "flex",
    alignItems: "center",
  },
  primaryInfoContainer: {
    flexGrow: 1,
    fontSize: "13px",
  },
  deliveryInfoHeader: {
    fontSize: "15px",
    fontWeight: 500,
  },
  skeletonItem: {
    display: "flex",
    marginBottom: theme.spacing(3),
  },
  skeletonRadioButtonContainer: {
    padding: theme.spacing(2),
    width: "40px",
    height: "40px",
  },
  skeletonTexts: {
    flexGrow: 1,
    marginRight: theme.spacing(2),
  },
  skeletonPrice: {
    display: "flex",
    alignItems: "center",
    flexBasis: "60px",
    "& > span": {
      flexGrow: 1,
    },
  },
  actionButtonContainer: {
    paddingRight: theme.clientType == "desktop" ? 0 : theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(1),
    },
  },
  catalogueDeliveryInfo: {
    marginTop: "-10px",
    paddingRight: theme.clientType == "desktop" ? 0 : theme.spacing(3),
    paddingLeft: theme.clientType == "desktop" ? 0 : theme.spacing(3),
    marginBottom: theme.spacing(2),
    "& .MuiAlert-message": {
      fontSize: "12px",
    },
  },
}));

function getDeliveryMethodPriceString(deliveryMethod, t) {
  let deliveryOptionsList = deliveryMethod.pickupLocations;
  if (deliveryMethod.deliveryOptions) {
    deliveryOptionsList = deliveryMethod.deliveryOptions;
  }
  console.log("Calculating price for", deliveryMethod, deliveryOptionsList);
  const prices = deliveryOptionsList.reduce((prices, deliveryOption) => {
    const delOptPrice = parseFloat(deliveryOption.price);

    if (prices.indexOf(delOptPrice) === -1) {
      prices.push(delOptPrice);
    }
    return prices;
  }, []);

  const sortedPrices = prices.sort();

  const minimumPrice = sortedPrices[0];
  if (sortedPrices.length === 1) {
    return minimumPrice == 0 ? t("Free") : formatCurrency(minimumPrice);
  }

  return (
    <>
      {t("Prices from")}
      <br />
      {formatCurrency(minimumPrice)}
    </>
  );
}

const SelectDeliveryOption = () => {
  const {
    deliveryMethods,
    setDeliveryOption: checkoutSetDeliveryOption,
    deliveryOption,
    deliveryOptionData,
    setCurrentStep,
    currentStep,
    type,
  } = useCheckout();
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const isMobileDevice = useIsMobileDevice();

  useEffect(() => {
    if (deliveryMethods?.length == 1) {
      setDeliveryMethod(deliveryMethods[0]);
    }
    // If we change due to catalogue or something?
    else if (deliveryMethods?.length > 1) {
      setDeliveryMethod(null);
    }
  }, [deliveryMethods]);

  console.log("Delivery methods", deliveryMethods);

  const handleEdit = () => {
    setCurrentStep("select-delivery-option");
  };

  const handleDeliveryMethod = (deliveryMethod) => {
    setDeliveryMethod(deliveryMethod);
  };

  console.log("The current step is", currentStep);

  const edit = currentStep === "select-delivery-option";

  return (
    <CheckoutCard>
      <Box
        display="flex"
        flexDirection={edit || isMobileDevice ? "column" : "row"}
      >
        <CheckoutCardTitle active={edit} complete={!edit}>
          <Trans>1. Delivery</Trans>
        </CheckoutCardTitle>
        {edit ? (
          <SelectDeliveryMethod
            handleDeliveryMethod={handleDeliveryMethod}
            deliveryMethod={deliveryMethod}
            checkoutType={type}
          />
        ) : (
          <SelectedDeliveryOption
            deliveryMethod={deliveryMethod}
            deliveryOption={deliveryOption}
            deliveryOptionData={deliveryOptionData}
            handleEdit={handleEdit}
          />
        )}
      </Box>
    </CheckoutCard>
  );
};

const SelectDeliveryMethod = ({
  deliveryMethod,
  handleDeliveryMethod,
  checkoutType,
}) => {
  const { catalogueMaxDeliveryTime } = useCheckout();
  const classes = useStyles();
  return (
    <>
      <SelectMethod
        deliveryMethod={deliveryMethod}
        handleChange={handleDeliveryMethod}
        checkoutType={checkoutType}
      />
      {catalogueMaxDeliveryTime?.days && (
        <div className={classes.catalogueDeliveryInfo}>
          <Alert severity="info">
            <Trans>
              Catalogues are shipped separately, normally within{" "}
              {{ days: catalogueMaxDeliveryTime.days }} days after the last
              photo day.
            </Trans>
          </Alert>
        </div>
      )}
      {deliveryMethod && <Options deliveryMethod={deliveryMethod} />}
    </>
  );
};

const SelectMethod = ({
  handleChange,
  deliveryMethod: currentDeliveryMethod,
  checkoutType,
}) => {
  const { deliveryMethods } = useCheckout();
  const { t } = useTranslation();
  const classes = useStyles();

  if (!deliveryMethods) {
    return <DeliveryMethodsSkeleton />;
  }

  return (
    <>
      <CheckoutCardList>
        {deliveryMethods.map((deliveryMethod, idx) => {
          let description =
            checkoutType === "PREPAID"
              ? getDescriptionLastPhotoday(deliveryMethod, t)
              : getDescription(deliveryMethod, t);

          const priceString = getDeliveryMethodPriceString(deliveryMethod, t);

          const selected =
            currentDeliveryMethod &&
            currentDeliveryMethod.type === deliveryMethod.type;

          return (
            <ListItemRadioButton
              key={deliveryMethod.name}
              labelId={"dm_" + deliveryMethod.name.replace(/\s/g, "_")}
              selected={selected}
              onClick={() => handleChange(deliveryMethod)}
              primaryText={deliveryMethod.name}
              secondaryText={description}
              action={
                <Box className={classes.deliveryPrice}>{priceString}</Box>
              }
            />
          );
        })}
      </CheckoutCardList>
    </>
  );
};

const getDescription = (deliveryMethod, t) => {
  switch (deliveryMethod.type) {
    case "store-pickup": {
      const storeDays = deliveryMethod.pickupLocations[0].deliveryTime.string;
      return t("Pick up your order in our store in %s", {
        postProcess: "sprintf",
        sprintf: [storeDays],
      });
    }
    case "postage": {
      const days = parseInt(deliveryMethod.deliveryOptions[0].deliverytime);
      if (days > 1) {
        return t("The order will be shipped within %d business days", {
          postProcess: "sprintf",
          sprintf: [days],
        });
      } else {
        return t("The order will be shipped within %d business day", {
          postProcess: "sprintf",
          sprintf: [days],
        });
      }
    }
    case "school-pickup":
      return t("We will deliver your order directly to the school/club");

    case "digital-delivery":
      return t(
        "The digital order content will be available instantly after purchase",
      );
  }
};

const getDescriptionLastPhotoday = (deliveryMethod, t) => {
  switch (deliveryMethod.type) {
    case "store-pickup": {
      const storeDays = deliveryMethod.pickupLocations[0].deliveryTime.string;
      return t("Pick up your order in our store in %s after last photo day", {
        postProcess: "sprintf",
        sprintf: [storeDays],
      });
    }
    case "postage": {
      return t("Orders typically ship shortly after Picture Day");
    }
    case "school-pickup": {
      const expiryDate = deliveryMethod.deliveryOptions[0].expire;
      return t("Orders will arrive at school after %s", {
        postProcess: "sprintf",
        sprintf: [expiryDate],
      });
    }
    case "digital-delivery":
      return t(
        "The digital order content will be available after last photo day",
      );
  }
};

const DeliveryMethodsSkeleton = () => {
  const classes = useStyles();

  return [...Array(3).keys()].map((idx) => {
    return (
      <div key={idx} className={classes.skeletonItem}>
        <div className={classes.skeletonRadioButtonContainer}>
          <Skeleton animation="pulse" variant="circle" />
        </div>
        <div className={classes.skeletonTexts}>
          <Skeleton animation="pulse" variant="text" />
          <Skeleton animation="pulse" variant="text" />
        </div>
        <div className={classes.skeletonPrice}>
          <Skeleton animation="pulse" variant="text" />
        </div>
      </div>
    );
  });
};

const SelectedDeliveryOption = ({
  deliveryOption,
  deliveryMethod,
  deliveryOptionData,
  handleEdit,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileDevice = useIsMobileDevice();
  console.log("Current delivery option", deliveryOption, deliveryMethod);
  const price = deliveryOption.price;
  const priceString = price == 0 ? t("Free") : formatCurrency(price);

  let deliveryInfo = null;

  if (deliveryMethod.type == "school-pickup") {
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {t("School/club delivery (%s)", {
            postProcess: "sprintf",
            sprintf: [priceString],
          })}
        </div>
        <div>
          {deliveryOptionData.subject.group}: {deliveryOptionData.subject.name}
        </div>
        <div className={classes.secondaryInfo}>
          {deliveryOptionData.contactInfo.name}
        </div>
        <div className={classes.secondaryInfo}>
          {deliveryOptionData.contactInfo.emailAddress}
        </div>
      </div>
    );
  } else if (deliveryMethod.type == "store-pickup") {
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {t("Store pickup (%s)", {
            postProcess: "sprintf",
            sprintf: [priceString],
          })}
        </div>
        <div>{deliveryOption.description}</div>
        <div className={classes.secondaryInfo}>
          {deliveryOption.pickupaddress1}
        </div>
        <div className={classes.secondaryInfo}>
          {deliveryOption.pickuppostalcode} {deliveryOption.pickuppostalcity}
        </div>
      </div>
    );
  } else if (deliveryMethod.type == "postage") {
    const { deliveryAddress } = deliveryOptionData;
    const address = {
      name: deliveryAddress.name,
      address1: deliveryAddress.address,
      address2: deliveryAddress.address2,
      postalCode: deliveryAddress.postalCode,
      city: deliveryAddress.city,
      state: deliveryAddress.state,
    };
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {deliveryOption.isExpress
            ? t("Ship by mail - Express (%s)", {
                postProcess: "sprintf",
                sprintf: [priceString],
              })
            : t("Ship by mail (%s)", {
                postProcess: "sprintf",
                sprintf: [priceString],
              })}
        </div>

        <Address address={address} className={classes.deliveryAddress} />
      </div>
    );
  } else if (deliveryMethod.type == "digital-delivery") {
    const { contactInfo } = deliveryOptionData;
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {t("Digital delivery (%s)", {
            postProcess: "sprintf",
            sprintf: [priceString],
          })}
        </div>
        <div>{contactInfo.emailAddress}</div>
        <div className={classes.secondaryInfo}>{contactInfo.name}</div>
      </div>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexGrow={1}
      pb={isMobileDevice ? 2 : 0}
    >
      <Box
        px={3}
        py={theme.clientType != "desktop" && !isMobileDevice ? 2 : 0}
      >
        {deliveryInfo}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        className={classes.actionButtonContainer}
      >
        <IconButton
          onClick={handleEdit}
          color="primary"
          aria-label={t("Edit delivery")}
        >
          <Icon>edit</Icon>
        </IconButton>
      </Box>
    </Box>
  );
};

const Options = ({ deliveryMethod, handleChange }) => {
  let MethodOptions = SchoolDeliveryOptions;
  switch (deliveryMethod.type) {
    case "store-pickup":
      MethodOptions = PickupDeliveryOptions;
      break;
    case "postage":
      MethodOptions = PostageDeliveryOption;
      break;
    case "digital-delivery":
      MethodOptions = DigitalDeliveryOption;
      break;
  }

  console.log("Type", deliveryMethod.type, MethodOptions);
  return (
    <MethodOptions
      deliveryMethod={deliveryMethod}
      handleChange={handleChange}
    />
  );
};

export default SelectDeliveryOption;
